import axios from 'axios';
import {ENDPOINT_URL, PORT_RANGE_ENTRIES_API} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	LOOKUP_LIMIT,
	optionalText,
	resolveCandidatesWithFallback,
	namedResponseRows,
} from '../../core/resolutionUtils';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';

/**
 * Return a Port Range row identifier.
 *
 * An ACL is identified by its name - the table's delete sends
 * {profileName: row.aclName} - so the name is the identifier.
 *
 * @param {object} item Canvas row
 * @return {*} identifier
 */
function rowId(item) {
	return item.profileName ?? item.id;
}

/**
 * Normalize a Canvas Port Range row.
 * @param {object} item Canvas row
 * @return {object|null} agent candidate
 */
function normalizeRow(item) {
	const id = rowId(item);
	const name = item.profileName ?? item.name;
	if (id == null || !name) return null;
	return {
		entity_type: 'port_range',
		entity_id: String(id),
		name: String(name),
		status: optionalText(item.status),
		site_id: null,
		site_name: null,
		serial_no: null,
	};
}

/**
 * Resolve Port Range candidates.
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolvePortRange(params) {
	const query = String(params.query || '').trim();
	const browse = params.browse === true;
	if (!query && !browse) throw new Error('lookup_query_required');
	const loadRows = async (search, limit) => {
		const searchParam = '';
		const url = `${ENDPOINT_URL}${PORT_RANGE_ENTRIES_API}?limit=${limit}` +
			`&offset=0${searchParam}`;
		const {data} = await axios.get(url);
		return namedResponseRows(
			decodeAgentResponse(data) || {},
			'aclRangeEntryProfileConfs',
		);
	};
	if (!query) {
		const limit = Math.min(
			Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
			LOOKUP_LIMIT,
		);
		const rows = await loadRows(null, limit);
		replaceResolvedEntities('port_range', rows, rowId);
		return {
			candidates: rows.map(normalizeRow).filter(Boolean).slice(0, limit),
		};
	}
	const {rows, candidates} = await resolveCandidatesWithFallback({
		query,
		loadRows,
		normalizeRow,
		fuzzy: params.fuzzy === true,
	});
	replaceResolvedEntities('port_range', rows, rowId);
	return {candidates};
}

/**
 * Return a retained Port Range row.
 * @param {string} entityId entity identifier
 * @return {object|null} Canvas row
 */
export function getResolvedPortRange(entityId) {
	return getResolvedEntity('port_range', entityId);
}
